import { supabase } from '@/lib/supabase';

/** Server STT: MediaRecorder clip -> stt edge function -> Groq Whisper.
 *  iOS Safari records audio/mp4 (AAC), Chrome records webm — both accepted. */

const FN_URL = `${import.meta.env.VITE_SUPABASE_URL}/functions/v1/stt`;

export interface Recorder {
  stream: MediaStream;
  analyser: AnalyserNode;
  stop: () => Promise<Blob>;
  cancel: () => void;
}

export async function startRecording(): Promise<Recorder> {
  const stream = await navigator.mediaDevices.getUserMedia({
    audio: { echoCancellation: true, noiseSuppression: true },
  });
  const ctx = new AudioContext();
  const source = ctx.createMediaStreamSource(stream);
  const analyser = ctx.createAnalyser();
  analyser.fftSize = 256;
  source.connect(analyser);

  const mime = MediaRecorder.isTypeSupported('audio/mp4')
    ? 'audio/mp4'
    : MediaRecorder.isTypeSupported('audio/webm;codecs=opus')
      ? 'audio/webm;codecs=opus'
      : '';
  const rec = new MediaRecorder(stream, mime ? { mimeType: mime } : undefined);
  const chunks: Blob[] = [];
  rec.ondataavailable = (e) => {
    if (e.data.size) chunks.push(e.data);
  };
  rec.start(250);

  const cleanup = () => {
    stream.getTracks().forEach((t) => t.stop());
    void ctx.close();
  };

  return {
    stream,
    analyser,
    stop: () =>
      new Promise<Blob>((resolve) => {
        rec.onstop = () => {
          cleanup();
          resolve(new Blob(chunks, { type: rec.mimeType || 'audio/webm' }));
        };
        rec.stop();
      }),
    cancel: () => {
      rec.onstop = null;
      try {
        rec.stop();
      } catch { /* already stopped */ }
      cleanup();
    },
  };
}

export async function transcribe(blob: Blob): Promise<string> {
  const { data } = await supabase.auth.getSession();
  const token = data.session?.access_token;
  if (!token) throw new Error('signed out');

  const res = await fetch(FN_URL, {
    method: 'POST',
    headers: {
      Authorization: `Bearer ${token}`,
      apikey: import.meta.env.VITE_SUPABASE_ANON_KEY,
      'Content-Type': blob.type || 'application/octet-stream',
    },
    body: blob,
  });
  if (res.status === 501) throw new Error('stt-not-configured');
  if (!res.ok) throw new Error(`stt failed (${res.status})`);
  const json = await res.json();
  return String(json.text ?? '').trim();
}

/** RMS level 0..1 from an analyser — drives the live waveform + warm VAD. */
export function level(analyser: AnalyserNode, buf?: Uint8Array<ArrayBuffer>): number {
  const data = buf ?? new Uint8Array(analyser.frequencyBinCount);
  analyser.getByteTimeDomainData(data);
  let sum = 0;
  for (let i = 0; i < data.length; i++) {
    const v = (data[i] - 128) / 128;
    sum += v * v;
  }
  return Math.sqrt(sum / data.length);
}
